import {
  Component,
  contentChildren,
  effect,
  forwardRef,
  inject,
  input,
  model,
  output,
  signal,
  InputSignal,
  ModelSignal,
  OutputEmitterRef,
  Signal
} from '@angular/core';
import { takeUntilDestroyed, outputToObservable, toObservable } from '@angular/core/rxjs-interop';
import { merge, switchMap, EMPTY, Observable } from 'rxjs';
import { IdService } from '../../services/id.service';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { ChipComponent } from './chip.component';
import type { ChangeEventOn, SelectionChangeEvent } from '../../domain/selectable.domain';

import type { ControlValueAccessor } from '@angular/forms';

/**
 * Chip-list
 * This component will track all its chip component descendants
 * and allow the user to select a single entry or multiple entries
 *
 * Dual-support control shape: `value` is the single source of truth and doubles as the
 * Angular Signal Forms native custom-control binding. The `ControlValueAccessor` below is a
 * thin adapter required only by `[(ngModel)]` / `[formControl]`.
 *
 * See `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
 */
@Component({
  providers: [ValueAccessorProviderFactory(ChipListComponent)],
  selector: 'td-wb-chip-list',
  templateUrl: 'chip-list.component.html',
  styleUrls: ['chip-list.component.scss']
})
export class ChipListComponent implements ControlValueAccessor {
  private readonly idService = inject(IdService);

  /** Single source of truth, and the Signal Forms native custom-control binding. */
  readonly value: ModelSignal<unknown | Array<unknown> | undefined> = model<unknown | Array<unknown> | undefined>(undefined);

  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();

  name: InputSignal<string> = input<string>();

  chips: Signal<ReadonlyArray<ChipComponent>> = contentChildren(forwardRef(() => ChipComponent), { descendants: true });
  private readonly chips$: Observable<ReadonlyArray<ChipComponent>> = toObservable(this.chips);

  private readonly disabledByOwner = signal(false);

  private readonly optionSelectionChanges$: ChangeEventOn<ChipComponent> = this.chips$.pipe(
    switchMap((chips: ReadonlyArray<ChipComponent>): Observable<SelectionChangeEvent<ChipComponent>> => {
      if (chips?.length) {
        return merge(...chips.map((item: ChipComponent) => outputToObservable(item.action)));
      }
      return EMPTY;
    })
  );

  /**
   * Single Selection.
   * Allows consumers to pick only one or many chip options
   * True (single) by default.
   */
  singleSelection: InputSignal<boolean> = input<boolean>(true);

  constructor() {
    this.chips$.pipe(takeUntilDestroyed()).subscribe((): void => {
      this.updateChipIds();
      this.updateChipNames();
    });

    toObservable(this.name).pipe(takeUntilDestroyed()).subscribe((): void => {
      this.updateChipNames();
    });

    // Imperative push of derived state into projected child components. This is the
    // sanctioned use of effect(): syncing signal state to a non-signal (imperative) API.
    effect((): void => {
      this.updateSelectedChipFromValue();
      this.applyDisabledState();
    });

    this.optionSelectionChanges$.pipe(
      takeUntilDestroyed()
    ).subscribe((event: SelectionChangeEvent<ChipComponent>): void => {
      if (this.singleSelection()) {
        this.value.set(event.source.selected ? event.source.item() : undefined);
      } else {
        const current = this.value();
        const valArray = Array.isArray(current) ? current : [];
        if (event.source.selected) {
          this.value.set([...valArray, event.source.item()]);
        } else {
          this.value.set(valArray.filter((item: unknown) => item !== event.source.item()));
        }
      }

      if (event.fromView) {
        this.onTouchCallback();
        this.onChangeCallback(this.value());
        this.touch.emit();
      }
    });
  }

  /**
   * Sets the model value. Implemented as part of ControlValueAccessor.
   * Owner -> control, so it must never emit onChangeCallback.
   *
   * @param value
   */
  writeValue(value: unknown | Array<unknown>): void {
    this.value.set(value);
    // Synchronous so callers observe updated children immediately; the constructor effect
    // is only a safety net for writes that arrive through the `[(value)]` model binding.
    this.updateSelectedChipFromValue();
  }

  // Implemented as part of ControlValueAccessor.
  registerOnChange(onChangeCallback: (value: unknown | Array<unknown> | undefined) => void): void {
    this.onChangeCallback = onChangeCallback;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnTouched(onTouchCallback: () => void): void {
    this.onTouchCallback = onTouchCallback;
  }

  // Implemented as part of ControlValueAccessor.
  setDisabledState(isDisabled: boolean): void {
    this.disabledByOwner.set(isDisabled);
  }

  private onChangeCallback: (_: unknown | Array<unknown> | undefined) => void = (): void => {
  };

  private onTouchCallback: () => void = (): void => {
  };

  private applyDisabledState(): void {
    const isDisabled = this.disabledByOwner();
    this.chips().forEach((chip: ChipComponent): void => {
      chip.disabled.set(isDisabled);
    });
  }

  private updateSelectedChipFromValue(): void {
    const chips = this.chips();
    const currentValue = this.value();
    if (chips !== undefined) {
      if (this.singleSelection()) {
        chips.forEach((chip: ChipComponent): void => {
          chip.setSelected(currentValue === chip.item());
        });
      } else { //multi
        const valueArray = Array.isArray(currentValue) ? currentValue : [];
        chips.forEach((chip: ChipComponent): void => {
          chip.setSelected(valueArray.includes(chip.item()));
        });
      }
    }
  }

  private updateChipNames(): void {
    const chips = this.chips();
    if (chips !== undefined) {
      chips.forEach((chip: ChipComponent): void => {
        if (this.name() !== undefined) {
          chip.name.set(this.name());
        } else {
          chip.name.set(chip.id());
        }
      });
    }
  }

  private updateChipIds(): void {
    const chips = this.chips();
    if (chips !== undefined) {
      chips.forEach((chip: ChipComponent): void => {
        if (chip.id() === undefined) {
          chip.id.set(this.getRadioId());
        }
      });
    }
  }

  private getRadioId(): string {
    return this.idService.generateNamespaceSequentialId('chips');
  }
}
